import { useRef, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type CHLD, mutationFunctions } from "@/models/chld";
import { type FAMI } from "@/models/fami";
import { type LANG, type SLNG, type ENRL, type CLEN } from "@/models";
import { useLeaData } from "@/queries.ts";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import Popup from "@/components/shared/Popup";
import { CHLDAddEditForm } from "@/components/records/child/CHLDAddEditForm";
import { FiEdit, FiEye, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";
import { BlockedDeletionDialog } from "@/components/shared/BlockedDeleteDialog";
import { useBlockedChildDeletion } from "./hooks/useBlockedChildDeletion";
import { getChildBlockedGroups } from "./utils/getChildBlockedGroups";
import { buildChildAddDefaults } from "./useCHLDFormLogic";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "ChildName", field: "ChildName", title: "Child Name" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  {
    id: "EffectiveStartDate",
    field: "EffectiveStartDate",
    title: "Effective Start Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Child Name", key: "ChildName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Effective Start Date", key: "EffectiveStartDate" },
];

function getMostRecentChild(records: CHLD[]): CHLD | undefined {
  if (records.length === 0) return undefined;
  return [...records].sort((a, b) => {
    const da = new Date(a.EffectiveStartDate ?? "").getTime();
    const db = new Date(b.EffectiveStartDate ?? "").getTime();
    return (
      (Number.isNaN(db) ? -Infinity : db) - (Number.isNaN(da) ? -Infinity : da)
    );
  })[0];
}

export default function CHLDRecordsTab({
  chldRecords,
  famiRecords = [],
  langRecords = [],
  slngRecords = [],
  enrlRecords = [],
  clenRecords = [],
}: {
  chldRecords: CHLD[];
  famiRecords?: FAMI[];
  langRecords?: LANG[];
  slngRecords?: SLNG[];
  enrlRecords?: ENRL[];
  clenRecords?: CLEN[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupChild, setPopupChild] = useState<CHLD | undefined>(undefined);
  const [childDefaults, setChildDefaults] = useState<Partial<CHLD> | undefined>(
    undefined
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [blockedDeletionOpen, setBlockedDeletionOpen] =
    useState<boolean>(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    data: CHLD;
    dateChanged: boolean;
  } | null>(null);
  const pendingDeleteNameRef = useRef<string>("");
  const shouldNavigateAfterDeleteRef = useRef<boolean>(false);

  const addEditForm = useForm<CHLD>({ defaultValues: {} });

  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const {
    pendingFamiDeletes,
    pendingLangDeletes,
    pendingSlngDeletes,
    pendingEnrlDeletes,
    pendingClenDeletes,
    addFamiDelete,
    addLangDelete,
    addSlngDelete,
    addEnrlDelete,
    addClenDelete,
    handleBlockedDeleteRecords,
    deletingRecords: isAnyBulkDeleting,
    reset: resetBlockedDeletion,
  } = useBlockedChildDeletion({
    leaData,
    famiRecords,
    langRecords,
    slngRecords,
    enrlRecords,
    clenRecords,
    blockedDeletionOpen,
  });

  const closePopup = () => {
    setPopupOpen(false);
    setPopupChild(undefined);
    setChildDefaults(undefined);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setSubmitConfirm(null);
    setBlockedDeletionOpen(false);
    resetBlockedDeletion();
    addEditForm.reset({});
    addEditForm.clearErrors();
  };

  const navigateAfterDelete = () => {
    if (shouldNavigateAfterDeleteRef.current) {
      navigate({
        to: "/$leaid/records/child",
        params: { leaid: leaContext.currentUserOrg.esiIdPath },
      });
    }
  };

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "CHLD",
      toastMessage: "Child record submitted.\nWaiting for validation...",
      onSuccess: closePopup,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "CHLD",
    toastMessage: () =>
      `${pendingDeleteNameRef.current}'s child record submitted for deletion`,
    successMessage: () =>
      `${pendingDeleteNameRef.current}'s child record deleted successfully`,
    onSuccess: () => {
      closePopup();
      navigateAfterDelete();
    },
  });

  const mutateFns = mutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = mutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<CHLD> = (x) => {
    if (!popupChild) {
      handleSubmitStart();
      mutateFns.handleSave(x);
      return;
    }
    const dateChanged =
      (popupChild.EffectiveStartDate ?? "") !== (x.EffectiveStartDate ?? "");
    setSubmitConfirm({ data: x, dateChanged });
  };

  const handleConfirmSubmit = () => {
    if (!submitConfirm) return;
    const data = submitConfirm.data;
    setSubmitConfirm(null);
    handleSubmitStart();
    mutateFns.handleSave(data);
  };

  const onDeleteClick = () => {
    if (popupChild) {
      const csppid = popupChild.CSPPID;
      const chldCountForId = chldRecords.filter(
        (r) => r.CSPPID === csppid
      ).length;

      const hasRelatedRecords =
        famiRecords.length > 0 ||
        langRecords.length > 0 ||
        slngRecords.length > 0 ||
        enrlRecords.length > 0 ||
        clenRecords.length > 0;

      if (chldCountForId <= 1 && hasRelatedRecords) {
        setPopupOpen(false);
        setBlockedDeletionOpen(true);
        return;
      }
    }
    setShowDeleteConfirm(true);
  };

  const executeChildDelete = () => {
    if (!popupChild) return;
    const name = [
      popupChild.ChildFirstName,
      popupChild.ChildMiddleName,
      popupChild.ChildLastName,
    ]
      .filter(Boolean)
      .join(" ");
    const csppid = popupChild.CSPPID;
    const chldCountForId = chldRecords.filter(
      (r) => r.CSPPID === csppid
    ).length;
    pendingDeleteNameRef.current = name;
    shouldNavigateAfterDeleteRef.current = chldCountForId === 1;
    setIsDeleting(true);
    handleDeleteStart();
    deleteMutateFns.handleSave(popupChild, true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    executeChildDelete();
  };

  const canEdit = leaContext.hasRole("CHLDEdit");

  const formId = "frmCHLD";

  const onAddButtonClick = () => {
    const mostRecent = getMostRecentChild(chldRecords);

    if (!mostRecent) {
      setChildDefaults(undefined);
      setPopupChild(undefined);
      setPopupOpen(true);
      addEditForm.reset({});
      addEditForm.clearErrors();
      return;
    }

    const prepopulated = buildChildAddDefaults(mostRecent);
    setChildDefaults(prepopulated);
    setPopupChild(undefined);
    setPopupOpen(true);
    addEditForm.reset(prepopulated);
    addEditForm.clearErrors();
  };

  const onEditButtonClick = (c: CHLD) => {
    setChildDefaults(undefined);
    setPopupChild(c);
    setPopupOpen(true);
    addEditForm.reset(c);
    addEditForm.clearErrors();
  };

  const tableData = chldRecords.map((record) => ({
    ProgramYear: record.ProgramYear,
    PreschoolName: leaContext?.currentOrgPreschools?.find(
      (x) => x.PreschoolCDSCode === record.PreschoolCode
    )?.PreschoolName,
    ChildName: `${record.ChildFirstName} ${record.ChildMiddleName} ${record.ChildLastName} ${record.ChildNameSuffix}`,
    LocalChildId: record.LocalChildId,
    CSPPID: record.CSPPID,
    EffectiveStartDate: record.EffectiveStartDate
      ? formatDateOnly(record.EffectiveStartDate)
      : "",
    __raw: record,
  }));

  const blockedSubtitle = popupChild
    ? `${[
        popupChild.ChildFirstName,
        popupChild.ChildMiddleName,
        popupChild.ChildLastName,
      ]
        .filter(Boolean)
        .join(" ")} · Local Child ID: ${popupChild.LocalChildId}`
    : "";

  const blockedGroups = getChildBlockedGroups({
    famiRecords,
    langRecords,
    slngRecords,
    enrlRecords,
    clenRecords,
    pendingFamiDeletes,
    pendingLangDeletes,
    pendingSlngDeletes,
    pendingEnrlDeletes,
    pendingClenDeletes,
    addFamiDelete,
    addLangDelete,
    addSlngDelete,
    addEnrlDelete,
    addClenDelete,
    leaContext,
    leaData,
  });

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export child records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["ChildName"], "child-record")}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add a child record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) => {
          onEditButtonClick(event.dataItem.__raw);
        }}
      />

      {popupOpen && (
        <Popup
          show={popupOpen}
          enforceFocus={!showDeleteConfirm && !submitConfirm}
          header={
            popupChild == null ? (
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: "1.25rem" }}
              >
                {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
                <span>
                  {canEdit ? "Add Child Record" : "View Child Record"}
                </span>
              </div>
            ) : (
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: "1.25rem" }}
              >
                {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
                <span>
                  {canEdit ? "Edit Child Record" : "View Child Record"}
                </span>
              </div>
            )
          }
          hasButton={false}
          submitFormId={canEdit ? formId : undefined}
          submitButtonText={
            popupChild ? (
              <span className="d-flex align-items-center">
                <FiEdit className="me-1" />
                Update
              </span>
            ) : (
              <span className="d-flex align-items-center">
                <FiSave className="me-1" />
                Save
              </span>
            )
          }
          submitButtonLoading={isSubmitting}
          submitButtonLoadingText="Submitting..."
          secondButtonText="Cancel"
          size="lg"
          onClose={closePopup}
          onSecondButtonClick={closePopup}
          canEdit={canEdit}
          leftFooterButton={
            canEdit && popupChild ? (
              <LoaderButton
                variant="danger"
                type="button"
                onClick={onDeleteClick}
                loading={isDeleting && isDeleteSubmitting}
                loadingText="Deleting..."
                className="d-flex align-items-center"
              >
                <FiTrash2 className="me-1" />
                Delete
              </LoaderButton>
            ) : undefined
          }
        >
          <Form
            id={formId}
            onSubmit={addEditForm.handleSubmit(onSubmit, () =>
              focusFirstInvalidField(`#${formId}`)
            )}
          >
            <CHLDAddEditForm
              selectedChild={popupChild}
              form={addEditForm}
              childDefaults={childDefaults}
            />
          </Form>
        </Popup>
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          recordKindLabel="Child record"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {submitConfirm && (
        <ConfirmModal
          title={
            submitConfirm.dateChanged
              ? "Confirm Effective Date Change"
              : "Confirm Record Update"
          }
          message={
            submitConfirm.dateChanged
              ? "The changes requested have a different effective date. They will insert a new record with these updates and automatically expire the old record."
              : "The changes requested will overwrite the existing record. If these changes became effective at a later date than when the record was originally input, please input the associated effective date."
          }
          confirmText="Submit"
          onCancel={() => setSubmitConfirm(null)}
          onConfirm={handleConfirmSubmit}
        />
      )}

      <BlockedDeletionDialog
        show={blockedDeletionOpen}
        onClose={closePopup}
        title="Cannot delete this Child record"
        subtitle={blockedSubtitle}
        description="Before this child record can be deleted, you'll need to delete the related records below."
        parentRecordKindLabel="Child record"
        groups={blockedGroups}
        onDeleteRecords={handleBlockedDeleteRecords}
        onDeleteParent={executeChildDelete}
        renderParentView={(onClose) =>
          popupChild ? (
            <Popup
              show={true}
              header={
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ fontSize: "1.25rem" }}
                >
                  <FiEye size={20} />
                  <span>View Child Record</span>
                </div>
              }
              hasButton={false}
              secondButtonText="Back"
              size="lg"
              onClose={onClose}
              onSecondButtonClick={onClose}
              canEdit={false}
              leftFooterButton={
                canEdit ? (
                  <LoaderButton
                    variant="danger"
                    type="button"
                    onClick={() => {
                      onClose();
                      setShowDeleteConfirm(true);
                    }}
                    loading={false}
                    loadingText="Deleting..."
                    className="d-flex align-items-center"
                  >
                    <FiTrash2 className="me-1" />
                    Delete
                  </LoaderButton>
                ) : undefined
              }
            >
              <CHLDAddEditForm
                selectedChild={popupChild}
                form={addEditForm}
                childDefaults={undefined}
              />
            </Popup>
          ) : null
        }
        deletingRecords={isAnyBulkDeleting}
        deletingParent={isDeleting && isDeleteSubmitting}
      />
    </>
  );
}
